import * as React from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { SVGs } from "../../assets";
import { Button, Divider, Text } from "../../components";
import type { GlobalServiceDetails, Product } from "../../apis/products";
import { APIs } from "../../apis";
import { useApi } from "../../hooks";
import type { DashboardCoords } from "./viewModel";
import { SystemServiceCode } from "../../stores";

function Loader({ showButton }: { showButton: boolean }) {
  return (
    <div className="flex w-full flex-col gap-[16px] sm:flex-row sm:items-center animate-pulse">
      <div className="flex flex-1 items-start gap-[16px] min-w-0">
        <div className="h-[47px] w-[47px] shrink-0 rounded-[10px] bg-divider" />
        <div className="flex flex-col gap-[10px] flex-1">
          <div className="h-[14px] w-2/5 rounded-[8px] bg-divider" />
          <div className="h-[14px] w-4/5 rounded-[8px] bg-divider" />
        </div>
      </div>

      <Divider className="border-divider" orientation="vertical" />

      <div className="flex w-full flex-col gap-[16px] sm:w-auto sm:flex-row sm:items-center">
        <div className="flex flex-wrap gap-[16px]">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div className="w-[calc(50%-8px)] sm:w-[128px]" key={idx}>
              <div className="h-[10px] w-2/3 rounded-[6px] bg-divider" />
              <div className="mt-[6px] h-[12px] w-full rounded-[6px] bg-divider" />
            </div>
          ))}
        </div>

        {showButton ? (
          <div className="h-[36px] w-full sm:w-[140px] rounded-[10px] bg-divider" />
        ) : null}
      </div>
    </div>
  );
}

export function TeleHealthBanner({
  onBook,
  coords,
  countryCode,
  ...props
}: {
  onBook?: (product: Product) => void;
  coords?: DashboardCoords;
  countryCode?: string;
  emptyText?: (args: { hasTelehealthData: boolean }) => string;
}) {
  const { t } = useTranslation();
  const { fetch, data, loading, error } = useApi<GlobalServiceDetails | null>();
  const emptyText =
    props.emptyText?.({
      hasTelehealthData: !!data && !error && !loading,
    }) ?? "";

  const toProduct = React.useCallback((): Product => {
    return {
      id: 0,
      name: data!.serviceName,
      code: SystemServiceCode.telehealth,
      imageUrl: null,
      totalAdvanceDiscountPrice: null,
      price: data!.pricing?.price ?? 0,
      totalAdvancePrice: data!.pricing?.price ?? 0,
      totalWalkInPrice: data!.pricing?.price ?? 0,
      pricing: data?.pricing,
      slotDuration: 0,
      description: data!.description,
      guidelines: null,
      allowAllLocations: false,
      status: "ENABLED",
      telehealth: true,
      partnerServiceId: 0,
      createdAt: new Date().toISOString(),
      modifiedAt: new Date().toISOString(),
      createdById: null,
      modifiedById: null,
      countryCode,
      partner_service: {
        id: 0,
        status: "ENABLED",
        partnerId: 0,
        serviceId: 0,
        service: {
          id: 0,
          name: data!.serviceName,
          code: SystemServiceCode.telehealth,
        },
        fees: [],
      },
    };
  }, [countryCode, data]);

  const fetchData = React.useCallback(async () => {
    if (!coords) return;
    try {
      await fetch(
        async () => {
          const res = await APIs.products.globalService({
            code: SystemServiceCode.telehealth,
            ...coords,
            countryCode,
          });
          return res.data?.service ?? null;
        },
        { showErrorAlert: false, resetDataOnError: true },
      );
    } catch {}
  }, [coords, countryCode, fetch]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div>
      {(data || loading) && (
        <>
          <Text level={2}>{t("dashboard.title")}</Text>
          <div
            className={twMerge(
              "my-[16px] rounded-[8px] border border-border bg-background p-[16px]",
              loading && "bg-transparent border-divider",
            )}
          >
            {loading ? (
              <Loader showButton={!!onBook} />
            ) : (
              <div className="flex w-full flex-col gap-[16px] sm:flex-row sm:items-center">
                <div className="flex flex-1 items-start gap-[16px] min-w-[250px]">
                  <img
                    src={SVGs.megaphone}
                    alt="megaphone"
                    className="w-[47px] h-[47px]"
                  />
                  <div className="flex flex-col gap-[4px] flex-1">
                    <Text className="font-semibold">
                      {`${data!.serviceName ?? t("dashboard.promo.title")}${t("dashboard.promo.titleSuffix")}`}
                    </Text>
                    <Text>{t("dashboard.promo.subtitle")}</Text>
                  </div>
                </div>
                <Divider className="border-border" orientation="vertical" />
                <div className="flex w-full flex-col gap-[16px] sm:w-auto sm:flex-row sm:items-center">
                  <div className="flex flex-wrap gap-[16px]">
                    {[
                      {
                        label: t("general.availability"),
                        value:
                          data!.availability ??
                          t("dashboard.promo.availabilityValue"),
                      },
                      {
                        label: t("general.price"),
                        value: data!.pricing?.free
                          ? t("general.free")
                          : data!.pricing?.price != null
                            ? String(data!.pricing.price)
                            : t("dashboard.promo.priceValue"),
                      },
                      {
                        label: t("general.language"),
                        value: data!.language ?? t("general.english"),
                      },
                      {
                        label: t("general.duration"),
                        value:
                          data!.duration ?? t("dashboard.promo.durationValue"),
                      },
                    ].map(({ label, value }, index) => (
                      <div className="min-w-[110px]" key={index}>
                        <Text>{label}</Text>
                        <Text className="font-semibold">{value}</Text>
                      </div>
                    ))}
                  </div>

                  {!!onBook && !!data && (
                    <div className="flex w-full sm:w-auto">
                      <Button
                        className="min-w-[140px] w-full sm:w-auto"
                        onClick={() => onBook(toProduct())}
                      >
                        {t("dashboard.bookNow")}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
      {emptyText && <Text className={"text-center"}>{emptyText}</Text>}
    </div>
  );
}
